// Artemis alerts (API-3779): the alerts conversation, alert messages in it, the alert cards Artemis's
// alert tools return, and the alert settings screen (artemisapi docs/client-integration-guide.md,
// "Alerts conversation").

// The user's alerts conversation: one per user, Kind "alerts", made by the server with their first alert.
export const isAlertsChat = row => !!row && row.kind === 'alerts'

// The alerts conversation first, then the rest in their order.
export const pinAlerts = rows => [...rows.filter(isAlertsChat), ...rows.filter(r => !isAlertsChat(r))]

// An alert message (AuthorType "alert") as a turn of its own: { kind: 'alert', html, alert, notice }.
// notice is true for Artemis's note about why a push or text couldn't go (Alert.Kind "notice").
export const alertTurn = m => {
  const alert = m.Alert && typeof m.Alert === 'object' ? m.Alert : {}
  return {
    id: `v3-${m.Id}`,
    kind: 'alert',
    messageId: m.Id,
    seq: m.Seq,
    html: m.Html || '',
    text: m.Text || '',
    alert,
    notice: alert.Kind === 'notice',
    status: 'complete',
    at: Date.parse(m.CreatedAt || '') || null,
    steps: [],
    citations: [],
    question: '',
    answer: ''
  }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// "Apr 23, 2027" from YYYY-MM-DD, without the time zone moving the day; "Apr 23" without the year.
export const longDate = (ymd, withYear = true) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd || '')
  if (!m) return ''
  return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}${withYear ? `, ${m[1]}` : ''}`
}

// What a deadline reminder's card shows besides its text: the date, whether it's an estimate, a
// changed date, and where to apply. → { label, date, estimate, change, applyUrl }, or null for other kinds.
export const alertExtras = alert => {
  const p = alert && alert.Payload
  if (!p || typeof p !== 'object' || alert.Kind !== 'deadline') return null
  const CHANGES = { moved: 'Date moved', published: 'Date confirmed' }
  const change = p.Change === 'moved' && p.Was ? `Moved from ${longDate(p.Was, false) || p.Was}` : CHANGES[p.Change] || null
  return {
    label: p.Label || null,
    date: longDate(p.Date),
    estimate: p.IsEstimate === true,
    change,
    applyUrl: /^https?:\/\//i.test(p.ApplyUrl || '') ? p.ApplyUrl : null
  }
}

// 14 → "2 weeks", 1 → "1 day", 10 → "10 days".
export const leadLabel = days => {
  const n = Number(days)
  if (!Number.isFinite(n)) return ''
  if (n > 0 && n % 7 === 0) return `${n / 7} week${n === 7 ? '' : 's'}`
  return `${n} day${n === 1 ? '' : 's'}`
}

// [14, 1] → "2 weeks and 1 day before".
export const leadDaysLabel = list => {
  const labels = [...(list || [])].sort((a, b) => b - a).map(leadLabel).filter(Boolean)
  if (labels.length === 0) return ''
  const joined = labels.length === 1 ? labels[0] : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
  return `${joined} before`
}

// "14, 1" → { days: [14, 1] } or { error }. The server takes 0 to 365 days, at most 6 of them.
export const MAX_LEAD_DAYS = 365
export const MAX_LEADS = 6
export const parseLeadDays = text => {
  const parts = String(text || '').split(/[\s,]+/).filter(Boolean)
  if (parts.length === 0) return { error: 'Add at least one lead time' }
  const days = []
  for (const part of parts) {
    if (!/^\d+$/.test(part)) return { error: 'Use whole days, like 14, 1' }
    const n = Number(part)
    if (n > MAX_LEAD_DAYS) return { error: `At most ${MAX_LEAD_DAYS} days` }
    if (!days.includes(n)) days.push(n)
  }
  if (days.length > MAX_LEADS) return { error: `At most ${MAX_LEADS} lead times` }
  return { days: days.sort((a, b) => b - a) }
}

// Where an alert can go. The inbox always gets it.
export const CHANNELS = [
  { key: 'inbox', label: 'Alerts inbox', fixed: true },
  { key: 'email', label: 'Email' },
  { key: 'push', label: 'Push to your phone' },
  { key: 'sms', label: 'Text message' }
]

// Push and text are chosen once phone verification is in (WEB-7146); until then the web offers the
// inbox and email.
const SELECTABLE = ['inbox', 'email']

// The channels to offer, from the server's AvailableChannels: [{ key, label, fixed }].
export const offeredChannels = (available = []) => {
  const on = new Set(['inbox', ...(Array.isArray(available) ? available : [])])
  return CHANNELS.filter(c => on.has(c.key) && SELECTABLE.includes(c.key))
}

// The channel list to save: what was picked from the offered channels, plus any channel the alert
// already had that isn't offered here (a text set up in chat stays), inbox first.
export const channelsAfter = (current = [], offered = [], picked = []) => {
  const offeredKeys = new Set(offered.map(c => c.key))
  const keep = (current || []).filter(k => !offeredKeys.has(k))
  const all = new Set(['inbox', ...picked.filter(k => offeredKeys.has(k)), ...keep])
  return CHANNELS.map(c => c.key).filter(k => all.has(k)).concat([...all].filter(k => !CHANNELS.some(c => c.key === k)))
}

// ["inbox", "email"] → "Alerts inbox · Email".
export const channelsLabel = list => (list || []).map(k => (CHANNELS.find(c => c.key === k) || { label: k }).label).join(' · ')

// A tag for an alert that isn't running: Paused, Done or Stopped (failed); null while active.
export const statusTag = status => ({ paused: 'Paused', done: 'Done', failed: 'Stopped' }[status] || null)

// "21:00" style local times; both or neither.
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/
export const quietHoursError = (start, end) => {
  if (!start && !end) return null
  if (!start || !end) return 'Set both a start and an end, or neither'
  if (!HHMM.test(start) || !HHMM.test(end)) return 'Use times like 21:00'
  if (start === end) return 'Start and end can’t be the same'
  return null
}

// The time zones to pick from: the browser's list when it has one, with the current one kept.
export const timeZoneOptions = (current = null) => {
  let zones = []
  try { zones = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [] } catch (e) { zones = [] }
  const browser = deviceTimeZone()
  return [...new Set([current, browser, ...zones].filter(Boolean))].sort()
}

export const deviceTimeZone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null } catch (e) { return null }
}

// An alert tool's step result as a card: { action, alert } for Kind "alert" (created, updated, paused,
// resumed, deleted), { action: 'listed', alerts } for Kind "alerts" (get_alerts), else null.
export const savedAlertFrom = result => {
  if (!result || typeof result !== 'object') return null
  if (result.Kind === 'alert' && result.Id) return { action: result.Action || 'created', alert: result }
  if (result.Kind === 'alerts' && Array.isArray(result.Items)) return { action: 'listed', alerts: result.Items }
  return null
}

export const alertActionLabel = action => ({
  created: 'Alert saved', updated: 'Alert updated', paused: 'Alert paused', resumed: 'Alert resumed', deleted: 'Alert deleted'
}[action] || 'Alert')

// The reminders a new alert will send (Upcoming on "created"), soonest first:
// [{ key, label, date, estimate, lead }].
export const upcomingRows = (upcoming = []) => (Array.isArray(upcoming) ? upcoming : []).map((u, i) => ({
  key: `${u.Milestone || 'm'}-${u.LeadDays}-${i}`,
  label: [u.Label, u.DrawName].filter(Boolean).join(' · '),
  date: longDate(u.Date),
  estimate: u.IsEstimate === true,
  lead: Number.isFinite(Number(u.LeadDays)) ? `${leadLabel(u.LeadDays)} before` : ''
}))

// An alert link opened in the browser (/artemis?conversation=ID&message=ID, from an alert email, push or
// text, or an @mention push): { conversation, message } or null. message is optional.
export const alertDeepLink = search => {
  const params = new URLSearchParams(search || '')
  const conversation = params.get('conversation')
  if (!conversation || !/^[\w-]{1,64}$/.test(conversation)) return null
  const message = params.get('message')
  return { conversation, message: message && /^[\w-]{1,64}$/.test(message) ? message : null }
}

// The settings to PUT, from the form: { Channels, TimeZone, QuietHoursStart, QuietHoursEnd }.
export const settingsPayload = form => ({
  Channels: ['inbox', ...(form.channels || []).filter(k => k !== 'inbox')],
  TimeZone: form.timeZone || null,
  QuietHoursStart: form.quietStart || null,
  QuietHoursEnd: form.quietEnd || null
})

// Why alert emails are off (Settings.EmailOff), in words.
export const emailOffText = off => {
  if (!off) return null
  if (off.Reason === 'bounced') return 'Alert emails are off because your email address bounced.'
  if (off.Reason === 'complained') return 'Alert emails are off because one was marked as spam.'
  return 'You turned off alert emails.'
}

// -- change watches (WEB-7167; artemisapi API-3901 to API-3904) --------------------------------------

// What a change watch can follow (its Focus). Season dates and warnings are a unit's: a statewide watch
// has neither. An empty Focus follows everything it can.
export const FOCUS = [
  { key: 'season_dates', label: 'Season dates', unitOnly: true },
  { key: 'application_dates', label: 'Application dates' },
  { key: 'documents', label: 'New regulations booklet' },
  { key: 'warnings', label: 'Fire and closure warnings', unitOnly: true }
]

export const focusOptions = hasUnit => FOCUS.filter(f => hasUnit || !f.unitOnly)

// ["season_dates"] → "Season dates"; [] → "Everything" (what the watch can follow).
export const focusLabel = (focus, hasUnit) => {
  const list = Array.isArray(focus) ? focus : []
  const options = focusOptions(hasUnit)
  if (list.length === 0 || options.every(o => list.includes(o.key))) return 'Everything'
  return options.filter(o => list.includes(o.key)).map(o => o.label).join(' · ')
}

// The Focus to PATCH from the boxes ticked: [] when all are (everything, including what's added later),
// or { error } when none is.
export const focusAfter = (picked, hasUnit) => {
  const options = focusOptions(hasUnit)
  const keys = options.map(o => o.key).filter(k => (picked || []).includes(k))
  if (keys.length === 0) return { error: 'Pick at least one' }
  return { focus: keys.length === options.length ? [] : keys }
}

// The boxes to tick for a watch's Focus: all of them when it follows everything.
export const focusPicked = (focus, hasUnit) => {
  const list = Array.isArray(focus) ? focus : []
  return focusOptions(hasUnit).map(o => o.key).filter(k => list.length === 0 || list.includes(k))
}

const CHANGE_GROUPS = { application_dates: 'Application dates', season_dates: 'Season dates', documents: 'Booklet' }

// A change alert's changes (Payload.Changes), grouped by category in the server's order:
// [{ category, title, rows: [{ key, label, before, after, kind }] }]. kind is changed, new or gone.
export const changeGroups = payload => {
  const changes = payload && Array.isArray(payload.Changes) ? payload.Changes : []
  const groups = []
  changes.forEach((ch, i) => {
    if (!ch || typeof ch !== 'object') return
    const category = ch.Category || 'other'
    let group = groups.find(g => g.category === category)
    if (!group) {
      group = { category, title: CHANGE_GROUPS[category] || 'Other changes', rows: [] }
      groups.push(group)
    }
    group.rows.push({
      key: `${category}-${i}`,
      label: ch.Label || '',
      before: ch.Before || null,
      after: ch.After || null,
      kind: ch.Before == null ? 'new' : ch.After == null ? 'gone' : 'changed'
    })
  })
  return groups
}

const WARNING_KINDS = { fire: 'Fire', closure: 'Closure order' }
const WARNING_SUBTYPES = { fire_restriction: 'Fire restriction', emergency_closure: 'Emergency closure' }

// A unit warning alert's extras (Payload.Warning, API-3904): { label, date, url, source }, or null.
export const warningExtras = payload => {
  const w = payload && payload.Warning
  if (!w || typeof w !== 'object') return null
  return {
    label: WARNING_KINDS[w.Type] || WARNING_SUBTYPES[w.Subtype] || 'Agency warning',
    date: longDate(w.AsOf),
    url: /^https?:\/\//i.test(w.Url || '') ? w.Url : null,
    source: w.Source || null
  }
}

// What a change alert shows instead of its text: { summary, groups } when it lists changes, else null
// (a warning, or an older message, renders its text).
export const changeExtras = alert => {
  const p = alert && alert.Payload
  if (!alert || alert.Kind !== 'change' || !p || typeof p !== 'object') return null
  const groups = changeGroups(p)
  if (groups.length === 0) return null
  return { summary: typeof p.Summary === 'string' && p.Summary.trim() ? p.Summary.trim() : null, groups }
}

// "today", "yesterday" or "Oct 12", in the browser's time zone.
export const dayLabel = (iso, now = Date.now()) => {
  const at = Date.parse(iso || '')
  if (!Number.isFinite(at)) return ''
  const day = d => { const x = new Date(d); return `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}` }
  if (day(at) === day(now)) return 'today'
  if (day(at) === day(now - 86400000)) return 'yesterday'
  const d = new Date(at)
  return `${MONTHS[d.getMonth()]} ${d.getDate()}${d.getFullYear() === new Date(now).getFullYear() ? '' : `, ${d.getFullYear()}`}`
}

// A change watch's line in Your alerts: "Checked today · last change Oct 12", or null for other kinds.
export const checkedLine = (alert, now = Date.now()) => {
  if (!alert || alert.Kind !== 'change') return null
  const checked = alert.LastCheckedAt ? `Checked ${dayLabel(alert.LastCheckedAt, now)}` : 'Not checked yet'
  return alert.LastChangeAt ? `${checked} · last change ${dayLabel(alert.LastChangeAt, now)}` : `${checked} · no changes yet`
}

// "Reminder" or "Watch", for a tag on an alert.
export const alertKindLabel = kind => ({ deadline: 'Reminder', change: 'Watch' }[kind] || null)

// -- the hub's Alerts section (WEB-7169) ---------------------------------------------------------------

// What an alert message is, for its row and its header: { kind, label, icon }. kind is deadline, change,
// warning (a change watch's unit warning), notice (Artemis's note about a push or text) or alert.
export const alertMessageKind = alert => {
  const a = alert || {}
  if (a.Kind === 'notice') return { kind: 'notice', label: 'Note', icon: 'mail' }
  if (a.Kind === 'deadline') return { kind: 'deadline', label: 'Deadline', icon: 'calendar' }
  const warning = warningExtras(a.Payload)
  if (warning) return { kind: 'warning', label: warning.label, icon: 'fire' }
  if (a.Kind === 'change') return { kind: 'change', label: 'Change', icon: 'swap' }
  return { kind: 'alert', label: 'Alert', icon: 'bell' }
}

// "CO elk unit 4" from a change watch's Payload; null without a state.
export const alertPlace = payload => {
  const p = payload && typeof payload === 'object' ? payload : {}
  if (!p.State) return null
  return [p.State, p.Species, p.Unit ? `unit ${p.Unit}` : null].filter(Boolean).join(' ')
}

const formatIn = (iso, timeZone, options) => {
  const at = Date.parse(iso || '')
  if (!Number.isFinite(at)) return ''
  try {
    return new Intl.DateTimeFormat('en-US', { ...options, ...(timeZone ? { timeZone } : {}) }).format(at)
      .replace(' AM', ' am').replace(' PM', ' pm')
  } catch (e) {
    return new Intl.DateTimeFormat('en-US', options).format(at).replace(' AM', ' am').replace(' PM', ' pm')
  }
}

// A deadline's own time where the agency is, "Apr 23, 11:00 pm MDT"; the day alone when the state
// published only a day; "(estimate)" while it's one.
export const deadlineWhen = payload => {
  const p = payload || {}
  const when = p.At
    ? formatIn(p.At, p.TimeZone, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })
    : longDate(p.Date, false)
  if (!when) return '—'
  return p.IsEstimate === true ? `${when} (estimate)` : when
}

const MILESTONE_FACT = { application_close: 'Closes', application_open: 'Opens', results: 'Results' }

// The reminder after this one: the alert's next lead time smaller than the one this message was for, taken
// from the deadline's day, at 9:00 (when reminders go out). "Apr 22, 9:00 am"; "Last reminder sent" when
// none is left. Alert.NextDueAt isn't it: that's when the scheduler next looks, at most a day away.
// A "date changed" message (LeadDays null) counts every lead still ahead of now.
export const nextReminder = (payload, record, now = Date.now()) => {
  const p = payload || {}
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(p.Date || '')
  if (!m || !record) return null
  const day = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  const sent = Number(p.LeadDays)
  const leads = [...(record.LeadDays || [])].map(Number).filter(Number.isFinite).sort((a, b) => b - a)
  const next = Number.isFinite(sent) && p.LeadDays != null
    ? leads.find(l => l < sent)
    : leads.find(l => day - l * 86400000 + 86400000 > now)
  if (next === undefined) return 'Last reminder sent'
  const d = new Date(day - next * 86400000)
  return `${longDate(d.toISOString().slice(0, 10), false)}, 9:00 am`
}

const capital = text => (text ? text[0].toUpperCase() + text.slice(1) : text)

// The three fact tiles under an alert: [{ k, v }]. record is the Alert that wrote it (GET Alerts), when
// it's still there. Deadlines: when, the draw, and the next reminder. Change watches: was, now, checked.
// Unit warnings: source, as of, checked. Notices have none.
export const alertFacts = (alert, record = null, now = Date.now()) => {
  const a = alert || {}
  const p = a.Payload && typeof a.Payload === 'object' ? a.Payload : {}
  const { kind } = alertMessageKind(a)
  if (kind === 'deadline') {
    const next = !record ? '—'
      : record.Status === 'paused' ? 'Paused'
      : record.Status === 'active' ? nextReminder(p, record, now) || '—'
      : 'Last reminder sent'
    return [
      { k: MILESTONE_FACT[p.Milestone] || p.Label || 'Date', v: deadlineWhen(p) },
      { k: 'Draw', v: [p.DrawName, p.Year].filter(Boolean).join(' ') || '—' },
      { k: 'Next reminder', v: next }
    ]
  }
  const checked = record && record.LastCheckedAt ? capital(dayLabel(record.LastCheckedAt, now)) : null
  if (kind === 'warning') {
    const w = warningExtras(p)
    return [
      { k: 'Source', v: w.source || '—' },
      { k: 'As of', v: w.date || '—' },
      { k: 'Checked', v: checked || 'Every 6 hours' }
    ]
  }
  if (kind === 'change') {
    const first = (Array.isArray(p.Changes) ? p.Changes : []).find(c => c && typeof c === 'object')
    if (!first) return []
    return [
      { k: 'Was', v: first.Before || 'Not listed' },
      { k: 'Now', v: first.After || 'Removed' },
      { k: 'Checked', v: checked || 'Daily' }
    ]
  }
  return []
}

// The link an alert offers: { label, url } (the state's application page, or the warning's order), or null.
export const alertLink = alert => {
  const a = alert || {}
  const extras = alertExtras(a)
  if (extras && extras.applyUrl) return { label: 'Open application', url: extras.applyUrl }
  const warning = warningExtras(a.Payload)
  if (warning && warning.url) return { label: 'Read the order', url: warning.url }
  return null
}

const CHANNEL_WORDS = { inbox: 'inbox', push: 'push', email: 'email', sms: 'text' }

// ["inbox", "push", "email"] → "inbox, push and email".
export const channelWords = list => {
  const words = (list || []).map(k => CHANNEL_WORDS[k] || k)
  return words.length < 2 ? words.join('') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`
}

// "Deadline reminder · sent today 9:00 am to inbox, push and email". The message doesn't record where it
// went, so the alert's channels stand in.
export const alertSentLine = (alert, at, record = null, now = Date.now()) => {
  const { kind, label } = alertMessageKind(alert)
  const what = kind === 'deadline' ? 'Deadline reminder' : kind === 'change' ? 'Change' : label
  const when = at ? `sent ${dayLabel(new Date(at).toISOString(), now)} ${formatIn(new Date(at).toISOString(), null, { hour: 'numeric', minute: '2-digit' })}` : null
  const to = record && Array.isArray(record.Channels) && record.Channels.length ? `to ${channelWords(record.Channels)}` : null
  return [what, [when, to].filter(Boolean).join(' ')].filter(Boolean).join(' · ')
}

// The "This reminder" row's line: "2 weeks and 1 day before · inbox, push, email · Denver time".
export const alertRuleLine = record => {
  if (!record) return 'This alert was deleted.'
  if (record.Status === 'paused') return 'Paused · nothing is checked or sent until you resume it'
  if (record.Status === 'done') return 'Finished'
  if (record.Status === 'failed') return record.LastError || 'Stopped'
  const channels = (record.Channels || []).map(k => CHANNEL_WORDS[k] || k).join(', ')
  const zone = record.TimeZone ? `${record.TimeZone.split('/').pop().replace(/_/g, ' ')} time` : null
  const what = record.Kind === 'change' ? checkedLine(record) : leadDaysLabel(record.LeadDays)
  return [what, channels, zone].filter(Boolean).join(' · ')
}

// The list's filter chips. Watches: what change watches sent (changes and unit warnings). Finished:
// what alerts that are no longer running sent (done, stopped or deleted). records: GET Alerts with
// includeFinished, by Id.
export const ALERT_FILTERS = [{ key: 'inbox', label: 'Inbox' }, { key: 'watches', label: 'Watches' }, { key: 'finished', label: 'Finished' }]
export const alertMatches = (turn, filter, records) => {
  const alert = (turn && turn.alert) || {}
  if (filter === 'watches') return ['change', 'warning'].includes(alertMessageKind(alert).kind)
  if (filter === 'finished') {
    if (!alert.AlertId || turn.notice) return false
    const record = records.get(alert.AlertId)
    return !record || record.Status === 'done' || record.Status === 'failed'
  }
  return true
}

// Which alert messages are unread, how many messages are new in the inbox, and how far Read can go. The
// server keeps one read mark per conversation (POST Read with a Seq), and its UnreadCount counts every
// message after it that the user didn't write: alerts and Artemis's replies. So the unread ones are the
// newest unreadCount of othersSeqs (the Seqs of those messages loaded), and an alert read out of order is
// kept here (readIds) until the older ones are.
// → { unread: Set of alert message ids, count: new in the inbox less the alerts read here, readUpTo }:
// readUpTo is 'all' when no alert is unread, else the Seq just before the oldest unread one (null when
// nothing before it can be marked).
export const alertReadState = (turns, unreadCount, readIds = new Set(), othersSeqs = []) => {
  const n = Math.max(0, Number(unreadCount) || 0)
  const alerts = (turns || []).filter(t => t.kind === 'alert' && Number.isFinite(t.seq))
  const seqs = othersSeqs.length ? othersSeqs : alerts.map(t => t.seq)
  const newSeqs = new Set([...seqs].sort((a, b) => b - a).slice(0, n))
  const fresh = alerts.filter(t => newSeqs.has(t.seq))
  const unread = new Set(fresh.filter(t => !readIds.has(t.messageId)).map(t => t.messageId))
  const count = Math.max(0, n - (fresh.length - unread.size))
  if (unread.size === 0) return { unread, count, readUpTo: 'all' }
  const oldest = Math.min(...fresh.filter(t => unread.has(t.messageId)).map(t => t.seq))
  return { unread, count, readUpTo: oldest > 1 ? oldest - 1 : null }
}

// The questions asked after one alert, and Artemis's answers: the turns after it until the next alert
// (Artemis's notes about a push or text don't count as one). A note has none. Artemis answers a question
// in the inbox about the alerts since its last reply, so this is where each one belongs (WEB-7173).
export const repliesAbout = (turns, messageId) => {
  const list = turns || []
  const at = list.findIndex(t => t.kind === 'alert' && t.messageId === messageId)
  if (at < 0 || list[at].notice) return []
  const after = []
  for (let i = at + 1; i < list.length && !(list[i].kind === 'alert' && !list[i].notice); i++) {
    if (list[i].kind !== 'alert') after.push(list[i])
  }
  return after
}
